import { useEffect, useState } from "react";
import {
    ControlMode,
    InputMode,
    PositionResolution,
    PositionUnits,
} from "utils/common-types";
import { radpsToRpm } from "utils/common";
import {
  MIN_STEP,
  deviceToPhysical,
  isDeviceRangeValid,
} from "utils/throttle";
import { Device } from "./useDevices";
import {
    ServoPulseTransformFn,
    packetThreadPoolApi,
} from "utils/packet-workers";
import {
    CStore, TagsToml,
} from "cstore/cstore";
import { tags } from "cstore/toml/toml";

// ToDo: hardcoded step sizes until we have localStorage for this
const RPM_STEP_SIZE = 5;
const AMP_STEP_SIZE = 0.1;

// Throttle-related settings for a device. Either invalidReason is supplied or
// all fields are guaranteed to be valid.
export type ThrottleConfig = {
    id: string;
    invalidReason: string;
} | {
    id: string;
    invalidReason: undefined;
    controlMode: ControlMode;
    physicalMax: number;
    stepSize: number;
    positionControlMinPos: number;
    positionControlMaxPos: number;
    positionControlHomePos: number;
    positionResolution: PositionResolution;
    positionUnits: PositionUnits;
};

// Helpful accessor functions
export const isValid = (cfg?: ThrottleConfig): boolean =>
    ( cfg !== undefined && cfg.invalidReason === undefined );

export const getControlMode = (cfg?: ThrottleConfig): ControlMode | undefined =>
    ( cfg !== undefined && cfg.invalidReason === undefined ) ?  cfg.controlMode : undefined;

export const getPhysicalMax = (cfg?: ThrottleConfig): number | undefined =>
    ( cfg !== undefined && cfg.invalidReason === undefined ) ?  cfg.physicalMax : undefined;

export const getStepSize = (cfg?: ThrottleConfig): number | undefined =>
    ( cfg !== undefined && cfg.invalidReason === undefined ) ?  cfg.stepSize : undefined;


type Listener = () => void;
const _listeners: Listener[] = [];
const _notify =
    () =>
        _listeners.forEach((l) => {
            try {
                l()
            } catch ( e ) {
                console.error("Unhandled exception in listener:", e);
            }
        });

const _removeListener = ( l: Listener ) => {
    const index = _listeners.indexOf(l);
    if ( index === -1 ) return;
    _listeners.splice(index, 1);
};

let _connectedDevice: Device | undefined;

let _throttleCfg: ThrottleConfig | undefined;

let _controlMode: ControlMode | undefined;

// should only be called by non-render code in useDevices.ts
// Must be called in two situations:
//  * change to _connectedDeviceHandle
//  * change to the element of _devices referenced by _connectedDeviceHandle
export const _updateConnectedDevice = ( connectedDevice: Device | undefined ) => {
    _connectedDevice = connectedDevice;
    handleUpdate();
    _notify();
};

const handleUpdate = () => {
    _throttleCfg = undefined;
    if ( _connectedDevice !== undefined && _connectedDevice.initialized && _connectedDevice.cstore ) {
        _controlMode = _connectedDevice.controlMode;
        _throttleCfg = calcThrottleConfig( _connectedDevice, _connectedDevice.cstore );
    }

    const inputConfig = _connectedDevice?.initialized ? _connectedDevice.inputConfig : undefined;

    let transformFn: ServoPulseTransformFn;
    if ( inputConfig !== undefined && inputConfig.inputMode === InputMode.USB ) {
        // A transform function should only be set for USB input mode
        const physicalMax = getPhysicalMax( _throttleCfg );

        if ( isValid( _throttleCfg ) && physicalMax !== undefined ) {
            // Normal case for USB input mode -- use physical units
            transformFn = ( devicePos: number ): number => {
                return deviceToPhysical( inputConfig.inputMode, inputConfig, physicalMax, devicePos, _controlMode )
            };
        }
    }
    packetThreadPoolApi.setServoPulseTransformFn( transformFn )
};

export const APPLY_CFG_TEXT = "please either Update Firmware with a CFW file (preferred) or Update Config with a cstore BIN file.";

const calcThrottleConfig = ( connectedDevice: Device, cstore: CStore ): ThrottleConfig => {
    if ( !connectedDevice.initialized ) {
        // Unreachable; see CUID 86a3kekaj
        throw new Error( "Uninitialized connected device" );
    }

    const invalid = ( invalidReason: string ): ThrottleConfig =>
        ({ id: connectedDevice.mcuSerialNum, invalidReason });

    if ( connectedDevice.needToUpdate ) {
        return invalid( "A firmware update is required to use the throttle." );
    }

    // Prevents exceptions from being thrown by device range utility functions.
    const inputConfig = connectedDevice.inputConfig;
    if ( !isDeviceRangeValid( inputConfig, connectedDevice.controlMode ) ) {
        return invalid( "Device range is invalid; " + APPLY_CFG_TEXT );
    }

    // Extract from cstore
    let maxMotorSpeedRPM: number | undefined;
    let motorCurrentLimit: number | undefined;
    let inputUpper: number | undefined; // position control max
    let inputCenter: number | undefined; // position control home
    let positionResolution: PositionResolution | undefined;
    let positionUnits: PositionUnits | undefined;
    for (let i = 0; i < cstore.storedTags.length; i++) {
        const tag = cstore.storedTags[i];
        const def = tag.getTagDefinition(tags as TagsToml);
        if ( def === undefined ) continue;
        switch ( def.name ) {
            case "CP_MaxMotorSpeed":
                maxMotorSpeedRPM = radpsToRpm(tag.getData() as number);
                break;
            case "PP_MotorCurrentLimit":
                motorCurrentLimit = tag.getData() as number;
                break;
            case "IP_Upper":
                inputUpper = tag.getData() as number;
                break;
            case "IP_Center":
                inputCenter = tag.getData() as number;
                break;
            case "PS_PositionResolution":
                positionResolution = tag.getData() as number;
                break;
            case "PS_PositionUnits":
                positionUnits = tag.getData() as number;
                break;
            default:
        }
    }

    if ( connectedDevice.controlMode === ControlMode.POSITION ) {
        if ( positionResolution === undefined || positionUnits === undefined ||
            positionResolution <= PositionResolution.UNKNOWN || positionUnits <= PositionUnits.UNKNOWN ) {

            return invalid( "Position resolution and/or units are invalid; " + APPLY_CFG_TEXT );
        }
    } else {
        positionResolution ??= PositionResolution.UNKNOWN;
        positionUnits ??= PositionUnits.UNKNOWN;
    }

    // Set config based on control mode and cstore values
    let physicalMax = 0; // Maximum throttle value in either direction, in physical units
    let stepSize = 0; // Step size, in physical units
    let positionControlMinPos = 0;
    let positionControlMaxPos = 0;
    let positionControlHomePos = 0;
    switch ( connectedDevice.controlMode ) {
        case ControlMode.TORQUE:
            physicalMax = motorCurrentLimit ?? physicalMax;
            stepSize = AMP_STEP_SIZE;
            break;
        case ControlMode.SPEED:
            physicalMax = maxMotorSpeedRPM ?? physicalMax;
            stepSize = RPM_STEP_SIZE;
            break;
        case ControlMode.POSITION:
            if ( inputUpper === undefined || inputCenter === undefined ) {
                return invalid( `Missing IP_Upper and/or IP_Center in cstore; ${ APPLY_CFG_TEXT }` );
            }
            positionControlMaxPos = inputUpper;
            positionControlHomePos = inputCenter;
            break;
    }
    if ( (connectedDevice.controlMode !== ControlMode.POSITION) && (!isFinite( stepSize ) || !isFinite( physicalMax ) || stepSize < MIN_STEP || ( physicalMax <= 0 || physicalMax < stepSize )) ) {
        return invalid( `Invalid physicalMax (${ physicalMax }) or stepSize (${ stepSize }); ${ APPLY_CFG_TEXT }` );
    }

    return {
        id: connectedDevice.mcuSerialNum,
        invalidReason: undefined,
        controlMode: connectedDevice.controlMode,
        physicalMax,
        stepSize,
        positionControlMinPos,
        positionControlMaxPos,
        positionControlHomePos,
        positionResolution,
        positionUnits,
    }
};

export const useThrottleConfig = () => {
    const [throttleConfig, setThrottleConfig] = useState<ThrottleConfig | undefined>( _throttleCfg );

    useEffect(() => {
        // Called after any of these events:
        //   * Device connect/disconnect
        // A call means the throttle config for the currently connected device was recalculated.
        const onChange = () => {
            setThrottleConfig( _throttleCfg );
        };
        _listeners.push(onChange);

        return () => _removeListener(onChange);
    }, []);

    return {
        throttleConfig
    };
};
